<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import AppMessage from '@uiKit/elements/Message.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import { formatAed } from '@shared/utils'

export type PaymentMethod = 'wallet' | 'gateway'

interface Props {
  modelValue: PaymentMethod
  walletBalance: number
  exceedsBalance: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: PaymentMethod): void
}>()

interface PaymentOption {
  key: PaymentMethod
  icon: PiIconName
  title: string
  subtitle: string
}

const paymentOptions: PaymentOption[] = [
  { key: 'wallet', icon: 'wallet', title: 'Platform Wallet', subtitle: '' },
  // Payment Gateway hidden until available — uncomment to re-enable
  // {
  //   key: 'gateway',
  //   icon: 'credit-card',
  //   title: 'Payment Gateway',
  //   subtitle: 'Card, Bank Transfer & more',
  // },
]

const walletSubtitle = computed(() => `Balance: ${formatAed(props.walletBalance)}`)
const isGatewaySelected = computed(() => props.modelValue === 'gateway')

const select = (method: PaymentMethod) => emit('update:modelValue', method)
</script>

<template>
  <Box direction="col" gap="3" class="payment">
    <Text type="page-title" class="payment__title">Payment Method</Text>
    <Box direction="col" gap="2" class="payment__options">
      <template v-for="option in paymentOptions" :key="option.key">
        <Box
          is="button"
          align="center"
          gap="3"
          type="button"
          class="payment__option"
          :class="{ 'payment__option--active': modelValue === option.key }"
          @click="select(option.key)"
        >
          <Box align="center" justify="center" class="payment__icon-box">
            <PiIcon :name="option.icon" class="payment__icon" />
          </Box>
          <Box direction="col" grow gap="1" class="payment__text">
            <Text type="subheading" is="span">{{ option.title }}</Text>
            <Text type="caption" is="span">
              {{ option.key === 'wallet' ? walletSubtitle : option.subtitle }}
            </Text>
          </Box>
          <Box
            align="center"
            justify="center"
            class="payment__radio"
            :class="{ 'payment__radio--checked': modelValue === option.key }"
          >
            <PiIcon
              v-if="modelValue === option.key"
              name="check"
              class="payment__radio-check"
            />
          </Box>
        </Box>
        <Box
          v-if="option.key === 'wallet' && exceedsBalance"
          align="start"
          gap="2"
          class="payment__alert payment__alert--warning"
        >
          <Text is="span" class="payment__alert-icon">⚠️</Text>
          <Text type="body-sm" weight="semibold" is="span" class="payment__alert-text">
            Insufficient wallet balance. Your balance is {{ formatAed(walletBalance) }}.
          </Text>
        </Box>
      </template>
    </Box>
    <AppMessage
      v-if="isGatewaySelected"
      color="warning"
      variant="simple"
      size="small"
      class="payment__gateway-msg"
      message="Payment Gateway is not available at the moment. Please use Platform Wallet to continue."
    />
  </Box>
</template>

<style scoped>
.payment__title {
  margin-bottom: 4px;
}

.payment__option {
  padding: 14px 18px;
  border-radius: 12px;
  border: 2px solid transparent;
  background: var(--p-surface-50);
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.15s,
    background 0.15s;
  width: 100%;
}

.payment__option--active {
  border-color: var(--p-primary-500);
  background: rgba(238, 239, 255, 0.4);
}

.payment__icon-box {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--p-primary-100);
  flex-shrink: 0;
}

.payment__icon {
  font-size: 18px;
  color: var(--p-primary-500);
}

.payment__radio {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid var(--p-surface-300);
  flex-shrink: 0;
  transition: all 0.15s;
}

.payment__radio--checked {
  background: var(--p-primary-500);
  border-color: var(--p-primary-500);
}

.payment__radio-check {
  font-size: 10px;
  color: var(--p-surface-0);
}

.payment__alert {
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid;
}

.payment__alert--warning {
  background: var(--p-warning-50);
  border-color: var(--p-warning-500);
}

.payment__alert-icon {
  font-size: 13px;
  line-height: 20px;
  flex-shrink: 0;
}

.payment__alert-text {
  line-height: 20px;
  color: var(--p-warning-700);
}

.payment__gateway-msg :deep(.p-message) {
  padding: 6px 10px;
}

.payment__gateway-msg :deep(.p-message-text) {
  font-size: 12px;
  line-height: 1.4;
}

.payment__gateway-msg :deep(.p-message-icon) {
  font-size: 12px;
  width: 12px;
  height: 12px;
}
</style>
